import type { CSSProperties, ReactElement } from "react";
import type { FooterSocialIcon } from "../../../types";

type IconConfig = {
  element: ReactElement;
  viewBox: string;
  width: number;
  height: number;
  style?: CSSProperties;
};

const squareIconStyle: CSSProperties = { flexShrink: 0, aspectRatio: "1 / 1" };
const shrinkOnlyStyle: CSSProperties = { flexShrink: 0 };

const ICON_CONFIG: Record<FooterSocialIcon, IconConfig> = {
  linkedin: {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    style: squareIconStyle,
    element: (
      <path
        fill="#fff"
        d="M19 3c.53 0 1.039.211 1.414.586.375.375.586.884.586 1.414V19c0 .53-.211 1.039-.586 1.414A1.993 1.993 0 0 1 19 21H5a1.993 1.993 0 0 1-1.414-.586A1.993 1.993 0 0 1 3 19V5c0-.53.211-1.039.586-1.414A1.993 1.993 0 0 1 5 3h14zm-.5 15.5V13.2c0-.864-.343-1.693-.955-2.305a3.266 3.266 0 0 0-2.305-.955c-.85 0-1.84.52-2.32 1.3V10.13h-2.79V18.5h2.79V13.57c0-.77.62-1.4 1.39-1.4.371 0 .727.148.99.41.262.263.41.619.41.99V18.5h2.79zM6.88 8.56a1.68 1.68 0 0 0 1.188-.492A1.68 1.68 0 0 0 8.56 6.88 1.68 1.68 0 0 0 6.88 5.19a1.68 1.68 0 0 0-1.195.494A1.68 1.68 0 0 0 5.19 6.88c0 .93.76 1.68 1.69 1.68zM8.27 18.5V10.13H5.5V18.5h2.77z"
      />
    ),
  },
  twitter: {
    viewBox: "0 0 14 14",
    width: 14,
    height: 14,
    style: shrinkOnlyStyle,
    element: (
      <path
        fill="#fff"
        d="M11.025.656h2.147L8.482 6.03 14 13.344H9.68L6.294 8.909 2.424 13.344H.275l5.016-5.75L0 .657h4.43L7.486 4.71zM10.27 12.056h1.19L3.78 1.877H2.504z"
      />
    ),
  },
  facebook: {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    style: squareIconStyle,
    element: (
      <path
        fill="#fff"
        d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5c0-1.93 1.57-3.5 3.5-3.5H16v3h-2c-.55 0-1 .45-1 1V12h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"
      />
    ),
  },
  youtube: {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    style: squareIconStyle,
    element: (
      // Glyphe MONOCHROME blanc comme toutes les autres icônes du système (le triangle est
      // évidé par fillRule evenodd, la pastille apparaît au travers) — jamais le badge rouge
      // de la marque : sur pastille jaune il jurait avec mail/linkedin/X.
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M21.8 8.001c-.13-.98-.76-1.74-1.65-1.87C18.14 5.66 12 5.66 12 5.66s-6.14 0-8.15.47c-.89.13-1.52.89-1.65 1.87C1.95 9.5 1.95 12 1.95 12s0 2.5.25 4c.13.98.76 1.74 1.65 1.87 2.01.47 8.15.47 8.15.47s6.14 0 8.15-.47c.89-.13 1.52-.89 1.65-1.87.25-1.5.25-4 .25-4s0-2.5-.25-3.999ZM10.2 15.5V8.5L16 12l-5.8 3.5Z"
      />
    ),
  },
  whatsapp: {
    viewBox: "0 0 24 24",
    // Le tracé WhatsApp remplit son viewBox bord à bord (pas de marge interne comme les
    // autres glyphes) : rendu quelques px plus petit pour s'inscrire dans la pastille.
    width: 19,
    height: 19,
    style: squareIconStyle,
    element: (
      // Même règle que YouTube : glyphe MONOCHROME blanc, jamais le badge vert de la marque.
      <path
        fill="#fff"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"
      />
    ),
  },
  mail: {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    style: squareIconStyle,
    element: (
      <path
        fill="#fff"
        d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm0 2v.4l8 5 8-5V6H4zm16 2.6-7.45 4.66a1 1 0 0 1-1.1 0L4 8.6V18h16V8.6z"
      />
    ),
  },
  phone: {
    viewBox: "0 0 24 24",
    width: 24,
    height: 24,
    style: squareIconStyle,
    element: (
      <path
        fill="#fff"
        d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.49a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.19 2.2z"
      />
    ),
  },
};

/** Social network SVG icon (LinkedIn, Twitter, Facebook, YouTube). */
export function SocialBrandIcon({ type }: { type: FooterSocialIcon | string }) {
  const icon = ICON_CONFIG[type as FooterSocialIcon];
  if (!icon) return null;
  return (
    <svg
      viewBox={icon.viewBox}
      width={icon.width}
      height={icon.height}
      role="img"
      aria-hidden="true"
      style={icon.style}
    >
      {icon.element}
    </svg>
  );
}

